import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { identifyUser, trackEvent } from '../lib/analytics';
import { formatDateLong } from '../lib/dateFormat';
import { destinoDespuesDeEntrar } from '../lib/rutaPendiente';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();
  const { t } = useTranslation('auth');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error'); // e.g. access_denied when the user cancels at Google

    if (oauthError || !code) {
      toast.error(t('auth_callback.toasts.invalid_response'));
      navigate('/login');
      return;
    }

    // Anti-CSRF state: single-use — read it, delete it, then compare against what
    // Google echoed back. Mismatch/absent → abort WITHOUT calling the backend.
    const storedState = sessionStorage.getItem('google_oauth_state');
    sessionStorage.removeItem('google_oauth_state');
    if (!state || !storedState || state !== storedState) {
      toast.error(t('auth_callback.toasts.invalid_response'));
      navigate('/login');
      return;
    }

    const redirectUri = window.location.origin + '/auth/google/callback';

    (async () => {
      try {
        const data = await handleOAuthCallback(code, redirectUri);
        identifyUser(data.user.id, { email: data.user.email, plan: data.user.plan, name: data.user.name });
        trackEvent('user_logged_in_google', { plan: data.user.plan });
        // Reddit Pixel SignUp: only when the backend reports a newly created account
        // (fresh Google signup or guest conversion). Tolerates the field being absent
        // (frontend deployed before backend) and window.rdt being absent (non-prod/adblock).
        if (data.is_new_account && window.rdt) window.rdt('track', 'SignUp');
        toast.success(data.user.name
          ? t('auth_callback.welcome_template', { name: data.user.name })
          : t('auth_callback.welcome_simple'));
        // post-t7-1 (E1): llega a donde iba (sobrevive la ida y vuelta en la misma pestana) o al Panel
        const destino = destinoDespuesDeEntrar();
        window.history.replaceState(null, '', destino);
        navigate(destino, { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail?.code === 'email_recently_deleted') {
          toast.error(t('blocked.recently_deleted', {
            date: formatDateLong(detail.blocked_until),
          }), { duration: 10000 });
        } else if (detail?.code === 'google_email_unverified') {
          // AS-5 (candado D-4): el backend solo usa el correo del token como llave de
          // identidad si Google lo declara verificado. La clave del copy es la MISMA que
          // usa el camino nativo — un solo texto para los dos mundos.
          toast.error(t('blocked.google_email_unverified'));
        } else {
          toast.error((typeof detail === 'string' && detail) || t('auth_callback.toasts.google_failed_fallback'));
        }
        navigate('/login');
      }
    })();
  }, [navigate, handleOAuthCallback, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e0e0e]">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00] mx-auto" />
        <p className="text-[#6a6a6a] text-sm">{t('auth_callback.loading')}</p>
      </div>
    </div>
  );
}
